import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronUp, X, Minimize2 } from 'lucide-react';
import { cn } from "@/lib/utils";

import { FunnelConsoleResult } from '../types/api';

interface FunnelConsoleProps {
  result?: FunnelConsoleResult;
  onClear: () => void;
  onSelectStep?: (stepId: string) => void;
}

type ConsoleState = 'hidden' | 'minimized' | 'expanded';

export function FunnelConsole({ result, onClear, onSelectStep }: FunnelConsoleProps) {
  const { t } = useTranslation('storefront');
  const [state, setState] = useState<ConsoleState>('hidden');

  React.useEffect(() => {
    if (result) {
      setState('minimized');
    }
  }, [result]);

  // Auto-dismiss success after 6s when minimized (unless there are warnings to read)
  React.useEffect(() => {
    if (!result || state !== 'minimized') return;
    const hasErrors = !result.success && result.errors && result.errors.length > 0;
    const hasWarnings = result.warnings && result.warnings.length > 0;
    if (hasErrors || hasWarnings) return;
    const timer = setTimeout(() => {
      setState('hidden');
      onClear();
    }, 6000);
    return () => clearTimeout(timer);
  }, [result, state, onClear]);

  if (!result || state === 'hidden') return null;

  const hasErrors = !result.success && result.errors && result.errors.length > 0;
  const hasWarnings = !!result.warnings && result.warnings.length > 0;
  const hasOperations = result.operations && result.operations.length > 0;

  const summaryParts: string[] = [];
  if (hasErrors && result.errors) {
    summaryParts.push(t('console.errorCount', { count: result.errors.length }));
  } else if (result.summary) {
    if (result.summary.mounted > 0) summaryParts.push(t('console.mounted', { count: result.summary.mounted }));
    if (result.summary.swapped > 0) summaryParts.push(t('console.swapped', { count: result.summary.swapped }));
    if (result.summary.unmounted > 0) summaryParts.push(t('console.unmounted', { count: result.summary.unmounted }));
    if ((result.summary.split || 0) > 0)
      summaryParts.push(t('console.abTest', { count: result.summary.split }));
  }
  const summaryText = summaryParts.length > 0 ? summaryParts.join(' · ') : t('console.noChanges');

  const handleClose = () => {
    setState('hidden');
    onClear();
  };

  const toggleSize = () => {
    setState(state === 'minimized' ? 'expanded' : 'minimized');
  };

  return (
    <div
      className={cn(
        'absolute bottom-4 left-4 z-40 overflow-hidden rounded-xl border shadow-lg transition-all duration-300 ease-in-out',
        hasErrors
          ? 'border-red-200 bg-white dark:border-red-800 dark:bg-slate-900'
          : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900',
        state === 'expanded' ? 'w-[480px]' : 'w-auto max-w-[400px]',
      )}
    >
      {/* Minimized bar */}
      <div
        className={cn(
          'flex cursor-pointer items-center gap-2.5 px-3 py-2 transition-colors',
          hasErrors
            ? 'hover:bg-red-50 dark:hover:bg-red-950/30'
            : 'hover:bg-slate-50 dark:hover:bg-slate-800',
        )}
        onClick={toggleSize}
      >
        {hasErrors ? (
          <XCircle className="h-4 w-4 shrink-0 text-red-500" />
        ) : hasWarnings ? (
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
        ) : (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
        )}

        <div className="min-w-0 flex-1">
          <p className={cn(
            'truncate text-xs font-medium',
            hasErrors ? 'text-red-700 dark:text-red-300' : 'text-slate-700 dark:text-slate-200',
          )}>
            {hasErrors
              ? `${t('console.saveFailed')} ${summaryText}`
              : hasWarnings
                ? `${t('console.saved')} · ${t('console.warningCount', { count: result.warnings!.length })}`
                : t('console.saved')}
          </p>
          {!hasErrors && summaryParts.length > 0 && (
            <p className="truncate text-[10px] text-slate-500 dark:text-slate-400">
              {summaryText}
            </p>
          )}
        </div>

        {(hasOperations || hasWarnings) && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleSize(); }}
            className="shrink-0 rounded p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            {state === 'expanded' ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronUp className="h-3.5 w-3.5" />
            )}
          </button>
        )}

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleClose(); }}
          className="shrink-0 rounded p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Expanded detail */}
      {state === 'expanded' && (
        <div className="border-t border-slate-200 dark:border-slate-700">
          <div className="max-h-[320px] overflow-y-auto overscroll-contain">
            <div className="space-y-2 p-3">
              {/* Errors */}
              {hasErrors && result.errors && (
                <div className="space-y-1.5">
                  {result.errors.map((error, index) => (
                    <div
                      key={index}
                      onClick={() => {
                        if (error.stepId && onSelectStep) {
                          onSelectStep(error.stepId);
                          setState('minimized');
                        }
                      }}
                      className={cn(
                        'rounded-lg border border-red-200 bg-red-50/50 px-3 py-2 dark:border-red-800 dark:bg-red-950/20',
                        error.stepId && onSelectStep && 'cursor-pointer hover:bg-red-50 dark:hover:bg-red-950/40',
                      )}
                    >
                      <p className="text-xs font-medium text-red-800 dark:text-red-200">{error.message}</p>
                      {error.stepName && (
                        <p className="mt-0.5 text-[10px] text-red-600 dark:text-red-400">
                          {error.stepName}
                          {error.stepId && onSelectStep && ` — ${t('console.clickToJump')}`}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Warnings (non-blocking — save succeeded) */}
              {hasWarnings && result.warnings && (
                <div className="space-y-1.5">
                  {result.warnings.map((warning, index) => (
                    <div
                      key={index}
                      onClick={() => {
                        if (warning.stepId && onSelectStep) {
                          onSelectStep(warning.stepId);
                          setState('minimized');
                        }
                      }}
                      className={cn(
                        'rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/20',
                        warning.stepId && onSelectStep && 'cursor-pointer hover:bg-amber-50 dark:hover:bg-amber-950/40',
                      )}
                    >
                      <p className="text-xs font-medium text-amber-800 dark:text-amber-200">{warning.message}</p>
                      {warning.stepName && (
                        <p className="mt-0.5 text-[10px] text-amber-600 dark:text-amber-400">
                          {warning.stepName}
                          {warning.stepId && onSelectStep && ` — ${t('console.clickToJump')}`}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Operations */}
              {!hasErrors && hasOperations && (
                <div className="space-y-1">
                  {result.operations!.map((op, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 rounded-md px-2 py-1.5"
                    >
                      {op.status === 'completed' && (
                        <CheckCircle2 className="h-3 w-3 shrink-0 text-green-500" />
                      )}
                      {op.status === 'failed' && (
                        <XCircle className="h-3 w-3 shrink-0 text-red-500" />
                      )}
                      {op.status === 'skipped' && (
                        <Minimize2 className="h-3 w-3 shrink-0 text-slate-400" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <Badge
                            variant="outline"
                            className={cn(
                              'h-4 px-1 text-[9px]',
                              op.status === 'skipped' && 'border-slate-200 text-slate-400 dark:border-slate-700',
                              op.status === 'completed' && 'border-green-200 text-green-600 dark:border-green-800 dark:text-green-400',
                              op.status === 'failed' && 'border-red-200 text-red-600 dark:border-red-800 dark:text-red-400',
                            )}
                          >
                            {op.type}
                          </Badge>
                          <span className={cn(
                            'truncate text-[11px] font-medium',
                            op.status === 'skipped' ? 'text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-200',
                          )}>
                            {op.stepName}
                          </span>
                        </div>
                        <p className="truncate text-[10px] text-slate-500 dark:text-slate-400">{op.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
